import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { publicApi, errMsg, getUtm } from "../lib/api";
import { track } from "./Analytics";
import { useT } from "../lib/i18n";
import { useSite } from "../lib/site";
import { telHref } from "./Layout";

function SuccessContact() {
  const { operator } = useSite();
  const { tl } = useT();
  if (!operator || !(operator.phone || operator.email)) return null;
  return (
    <p className="mt-3 text-sm text-slate-700 break-words" data-testid="success-contact">{tl("Questions? Contact us:")}{" "}
      {operator.phone && <a href={telHref(operator.phone)} className="underline text-amber-800 whitespace-nowrap" data-testid="success-contact-phone">{operator.phone}</a>}
      {operator.phone && operator.email && " · "}
      {operator.email && <a href={`mailto:${operator.email}`} className="underline text-amber-800 [overflow-wrap:anywhere] whitespace-nowrap" data-testid="success-contact-email">{operator.email}</a>}
    </p>
  );
}

export const useTl = () => useT().tl;

const input = "w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500";

function Field({ f, value, onChange }) {
  const tl = useTl();
  const { t } = useT();
  const id = `f-${f.name}`;
  const common = { id, name: f.name, required: f.required, value: value ?? "", onChange: (e) => onChange(f.name, e.target.value), className: input, "data-testid": `field-${f.name}` };
  if (f.type === "multi")
    return (
      <fieldset className="sm:col-span-2" data-testid={`field-${f.name}`}>
        <legend className="block text-sm font-medium text-slate-800 mb-2">{tl(f.label)}{f.required && " *"}</legend>
        <div className="flex flex-wrap gap-3">{f.options.map(([v, l]) => (
          <label key={v} className="flex items-center gap-2 text-sm border rounded-lg px-3 py-2 bg-white">
            <input type="checkbox" className="accent-amber-700" checked={(value || []).includes(v)} onChange={(e) => onChange(f.name, e.target.checked ? [...(value || []), v] : (value || []).filter((x) => x !== v))} data-testid={`field-${f.name}-${v}`} />{tl(l)}
          </label>))}</div>
      </fieldset>
    );
  if (f.type === "checkbox")
    return (
      <label className="flex items-start gap-3 text-sm text-slate-700 sm:col-span-2" htmlFor={id}>
        <input id={id} type="checkbox" className="mt-1 h-4 w-4 accent-amber-700" checked={!!value} required={f.required} onChange={(e) => onChange(f.name, e.target.checked)} data-testid={`field-${f.name}`} />
        <span>{tl(f.label)}{f.required && " *"}</span>
      </label>
    );
  return (
    <div className={f.wide ? "sm:col-span-2" : ""}>
      <label htmlFor={id} className="block text-sm font-medium text-slate-800 mb-1">{tl(f.label)}{f.required && " *"}</label>
      {f.type === "select" ? (
        <select {...common}><option value="">{t("common.select")}</option>{f.options.map(([v, l]) => <option key={v} value={v}>{tl(l)}</option>)}</select>
      ) : f.type === "textarea" ? <textarea rows={4} {...common} minLength={f.min} maxLength={f.max || 1500} /> : <input type={f.type || "text"} {...common} maxLength={f.max} pattern={f.pattern} inputMode={f.inputMode} />}
      {f.hint && <p className="text-xs text-slate-500 mt-1">{tl(f.hint)}</p>}
    </div>
  );
}

export function SmartForm({ fields, endpoint, initial = {}, extra = {}, notice, submitLabel, testid, successText }) {
  const [v, setV] = useState(initial);
  const [state, setState] = useState({ loading: false, error: "", done: null });
  const [started, setStarted] = useState(false);
  const tl = useTl();
  const { t, lang } = useT();
  const fieldLabel = (k) => tl(fields.find((f) => f.name === k)?.label || k);
  const pyMsg = (x) => {
    const m = String(x.msg).replace("Value error, ", "");
    if (lang !== "hi") return m;
    if (x.type === "missing") return tl("Field required");
    if (x.type === "string_too_short") return tl("Must be at least {n} characters").replace("{n}", x.ctx?.min_length ?? "");
    if (x.type === "value_error" && /email/i.test(m)) return tl("Enter a valid email address");
    const out = tl(m);
    return out === m ? tl("Please check this value") : out;
  };
  const errText = (err) => {
    if (err.empty) return tl("Please fill in the required fields: {labels}").replace("{labels}", err.empty.map((f) => tl(f.label)).join(", "));
    if (err.multi) return t("common.multiRequired", { label: tl(err.multi.label) });
    const d = err?.response?.data?.detail;
    if (Array.isArray(d)) return d.map((x) => `${fieldLabel(x.loc?.slice(-1)[0])}: ${pyMsg(x)}`).join("; ");
    return d ? tl(d) : t("common.genericError");
  };
  const onChange = (k, val) => { if (!started) { setStarted(true); track("form_started", { form: testid }); } setV((s) => ({ ...s, [k]: val })); };
  const submit = async (e) => {
    e.preventDefault();
    const empty = fields.filter((f) => f.required && f.type !== "multi" && (f.type === "checkbox" ? !v[f.name] : !String(v[f.name] ?? "").trim()));
    if (empty.length) { setState({ loading: false, error: { empty }, done: null }); return; }
    const multi = fields.find((f) => f.type === "multi" && f.required && !(v[f.name] || []).length);
    if (multi) { setState({ loading: false, error: { multi }, done: null }); return; }
    setState({ loading: true, error: null, done: null });
    try {
      const payload = { ...extra, ...v, utm: getUtm(), landing_page: window.location.pathname };
      Object.keys(payload).forEach((k) => payload[k] === "" && fields.find((f) => f.name === k && !f.required) && delete payload[k]);
      const r = await publicApi.post(endpoint, payload);
      setState({ loading: false, error: null, done: r.data });
      track("form_completed", { form: testid });
    } catch (err) {
      setState({ loading: false, error: err, done: null });
    }
  };
  if (state.done)
    return (
      <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-8" data-testid={`${testid}-success`} role="status">
        <CheckCircle2 className="h-8 w-8 text-emerald-700" />
        <p className="text-xl font-bold mt-3">{t("common.thankYou")}</p>
        <p className="mt-2 text-slate-700">{t("common.refIs")} <span className="font-mono font-bold" data-testid={`${testid}-reference`}>{state.done.reference}</span>. {tl(state.done.message || successText)}</p>
        <SuccessContact />
        <Link to="/" className="inline-block mt-4 underline text-amber-800">{t("common.backHome")}</Link>
      </div>
    );
  return (
    <form onSubmit={submit} className="bg-white border rounded-2xl p-6 sm:p-8 grid sm:grid-cols-2 gap-5" data-testid={testid} noValidate>
      {fields.map((f) => <Field key={f.name} f={f} value={v[f.name]} onChange={onChange} />)}
      <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" value={v.website || ""} onChange={(e) => onChange("website", e.target.value)} />
      {notice && <p className="sm:col-span-2 text-xs text-slate-600 bg-slate-50 border rounded-lg p-3" data-testid={`${testid}-notice`}>{tl(notice)}</p>}
      {state.error && <p className="sm:col-span-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" role="alert" data-testid={`${testid}-error`}>{errText(state.error)}</p>}
      <div className="sm:col-span-2">
        <button type="submit" disabled={state.loading} className="inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-800 disabled:opacity-60 text-white font-semibold py-3 px-6 rounded-lg transition-colors" data-testid={`${testid}-submit`}>
          {state.loading && <Loader2 className="h-4 w-4 animate-spin" />}{tl(submitLabel)}
        </button>
      </div>
    </form>
  );
}

const PHONE = { name: "phone", label: "Mobile number", required: true, type: "tel", inputMode: "tel", max: 14, hint: "10-digit Indian mobile number" };
export const STATES = ["Rajasthan", "Madhya Pradesh", "Gujarat", "Haryana", "Delhi", "Uttar Pradesh", "Punjab", "Maharashtra", "Other"].map((s) => [s, s]);

export const APPLICATION_FIELDS = [
  { name: "full_name", label: "Full name", required: true, max: 100 },
  { name: "business_name", label: "Business name (if any)", max: 150 },
  PHONE, { name: "email", label: "Email", required: true, type: "email" },
  { name: "city", label: "City / district", required: true },
  { name: "state", label: "State", required: true, type: "select", options: STATES },
  { name: "experience", label: "Business or sales experience", required: true, type: "textarea", wide: true },
  { name: "intended_market", label: "Intended market (customer types)", required: true, wide: true },
  { name: "service_territory", label: "Service territory (districts / towns you can serve)", required: true, wide: true },
  { name: "business_type", label: "Business type", required: true, type: "select", options: [["individual", "Individual / new business"], ["solar_installer_epc", "Solar installer / EPC"], ["electrical_dealer", "Electrical or inverter dealer"], ["distributor", "Distributor"], ["sales_professional", "Sales professional"], ["other", "Other"]] },
  { name: "services_of_interest", label: "Solar services of interest", required: true, type: "multi", options: [["residential", "Residential rooftop"], ["commercial", "Commercial & industrial"], ["survey", "Site surveys"], ["installation_coordination", "Installation coordination"]] },
  { name: "preferred_tier", label: "Preferred plan", required: true, type: "select", options: [["starter-partner", "Starter Partner"], ["growth-partner", "Growth Partner"], ["network-partner", "Network Partner"], ["undecided", "Not sure yet"]] },
  { name: "sales_capacity", label: "Expected sales capacity", required: true, type: "select", options: [["1-2 per month", "1–2 systems per month"], ["3-5 per month", "3–5 per month"], ["6-10 per month", "6–10 per month"], ["10+ per month", "More than 10"]] },
  { name: "source", label: "How did you hear about us?", required: true, type: "select", options: [["search", "Search engine"], ["social", "Social media"], ["referral", "Referral"], ["event", "Event"], ["other", "Other"]] },
  { name: "consent_privacy", type: "checkbox", required: true, label: "I have read the Privacy Policy and Terms, and agree that my details will be used to assess this application." },
  { name: "consent_contact", type: "checkbox", required: true, label: "I agree to be contacted by phone, email or WhatsApp about my application." },
  { name: "agreement_ack", type: "checkbox", label: "I understand plans are drafts, no fee is collected now, and income, leads and approvals are not guaranteed." },
];

export const ENQUIRY_FIELDS = [
  { name: "name", label: "Your name", required: true }, PHONE, { name: "email", label: "Email (optional)", type: "email" },
  { name: "city", label: "City", required: true }, { name: "state", label: "State", required: true, type: "select", options: STATES },
  { name: "pincode", label: "PIN code", max: 6, inputMode: "numeric" },
  { name: "requirement", label: "System type", type: "select", options: [["on-grid", "On-grid"], ["off-grid", "Off-grid"], ["hybrid", "Hybrid"], ["not-sure", "Not sure"]] },
  { name: "monthly_bill", label: "Average monthly bill (₹)", inputMode: "numeric", max: 10 },
  { name: "roof_type", label: "Roof type", type: "select", options: [["rcc", "RCC / concrete"], ["tin", "Tin / metal sheet"], ["other", "Other"]] },
  { name: "message", label: "Anything else?", type: "textarea", wide: true },
];

export const CI_FIELDS = [
  { name: "business_name", label: "Business name", required: true }, { name: "name", label: "Contact person", required: true }, PHONE,
  { name: "email", label: "Email", type: "email" }, { name: "city", label: "City", required: true }, { name: "state", label: "State", required: true, type: "select", options: STATES },
  { name: "connected_load_kw", label: "Sanctioned / connected load (kW)" }, { name: "roof_area", label: "Usable roof area (sq ft)" },
  { name: "requirement", label: "Preferred model", type: "select", options: [["capex", "CAPEX (own the plant)"], ["opex", "OPEX / RESCO"], ["not-sure", "Not sure"]] },
  { name: "message", label: "Project details", type: "textarea", wide: true },
];

export const CUSTOMER_CONSENT = [
  { name: "consent_privacy", type: "checkbox", required: true, label: "I agree that my details will be used to respond to this enquiry, as described in the Privacy Policy." },
  { name: "consent_share_with_partner", type: "checkbox", label: "I agree that my details may be shared with one or more assigned local partners who may contact me about this enquiry." },
];

export const GRIEVANCE_FIELDS = [
  { name: "name", label: "Your name", required: true }, PHONE, { name: "email", label: "Email (optional)", type: "email" },
  { name: "category", label: "Category", required: true, type: "select", options: [["customer_service", "Customer service"], ["partner_programme", "Partner programme"], ["lead_dispute", "Lead dispute"], ["commission_dispute", "Commission dispute"], ["privacy", "Privacy / data"], ["other", "Other"]] },
  { name: "related_reference", label: "Related reference number (if any)" },
  { name: "description", label: "Describe the issue", required: true, type: "textarea", wide: true, max: 3000, min: 10 },
  { name: "consent_privacy", type: "checkbox", required: true, label: "I agree that my details will be used to handle this complaint." },
];
